import Link from 'next/link';
import { memo, useCallback, useMemo } from 'react';

import { ExternalLinkIcon } from '@air/design-icons';
import { FileStatusIconCompleted, FileStatusSubtitle, FileStatusTitle } from '@air/feature-file-status/ui';

import { getTaskPaneFileStatusIconSize } from '~/components/FileStatusTrackingPane/getTaskPaneFileStatusIconSize';
import { PaneItemTW } from '~/components/FileStatusTrackingPane/PaneItemTW';
import { TaskPaneActionLink } from '~/components/FileStatusTrackingPane/TaskPaneActionLink';
import { TaskPaneDismissButton } from '~/components/FileStatusTrackingPane/TaskPaneDismissButton';
import { createAllContentAssetRoute, createBoardClipRoute } from '~/constants/routes';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { type DuplicateAssetTask, type GetCompletedMetadata } from '~/store/tasks/types';

export type DuplicateAssetTaskCompletedPanelItemProps = {
  localTaskId: string;
  metadata: GetCompletedMetadata<DuplicateAssetTask>;
  onClear: (localTaskId: string) => void;
};

const getDuplicateAssetCompletedTitle = ({ isFile, isUi2026 }: { isFile: boolean; isUi2026: boolean }) => {
  if (isUi2026) {
    return isFile ? 'File duplicated' : 'Asset duplicated';
  }

  return 'Duplicated successfully';
};

export const DuplicateAssetTaskCompletedPanelItem = memo(
  ({
    localTaskId,
    metadata: { sourceItemTitle, duplicatedRootClips, targetBoardId, isFile },
    onClear,
  }: DuplicateAssetTaskCompletedPanelItemProps) => {
    const { ui2026 } = useTemporaryFeatureFlags();
    const itemLabel = isFile ? 'file' : 'asset';
    const clipId = duplicatedRootClips?.[0]?.id;
    const clipTitle = duplicatedRootClips?.[0]?.title || sourceItemTitle;
    const handleClear = useCallback(() => onClear(localTaskId), [localTaskId, onClear]);

    const itemRoute = useMemo(() => {
      if (!clipId) {
        return null;
      }

      if (targetBoardId) {
        return createBoardClipRoute({ boardId: targetBoardId, boardTitle: '', clipId });
      }

      return createAllContentAssetRoute(clipId);
    }, [clipId, targetBoardId]);

    return (
      <PaneItemTW
        avatar={<FileStatusIconCompleted size={getTaskPaneFileStatusIconSize(ui2026)} />}
        buttons={
          <>
            {ui2026 && itemRoute ? (
              <TaskPaneActionLink
                aria-label={`View duplicated ${itemLabel} ${clipTitle}`}
                data-testid="DUPLICATED_ASSET_VIEW_LINK"
                href={itemRoute}
                icon={<ExternalLinkIcon size="sm" />}
                rel="noopener noreferrer"
              >
                View
              </TaskPaneActionLink>
            ) : null}
            <TaskPaneDismissButton data-testid="DUPLICATED_ASSET_CLEAR_BUTTON" label="Clear" onClick={handleClear} />
          </>
        }
        subtitle={
          ui2026 ? (
            <FileStatusSubtitle className="truncate" data-testid="DUPLICATED_ASSET_SUCCESS_SUBTITLE">
              {clipTitle}
            </FileStatusSubtitle>
          ) : (
            <FileStatusSubtitle className="truncate" data-testid="DUPLICATED_ASSET_SUCCESS_SUBTITLE">
              {itemLabel.charAt(0).toUpperCase()}
              {itemLabel.slice(1)} &quot;
              {itemRoute ? (
                <Link
                  className="font-semibold ads:text-accent text-altitude-9 ads:hover:text-accent-hover hover:text-altitude-11 hover:underline"
                  data-testid="DUPLICATED_ASSET_CLIP_LINK"
                  href={itemRoute}
                  rel="noopener noreferrer"
                >
                  {clipTitle}
                </Link>
              ) : (
                clipTitle
              )}
              &quot; is ready to use.
            </FileStatusSubtitle>
          )
        }
        title={
          <FileStatusTitle data-testid="DUPLICATED_ASSET_SUCCESS_TITLE">
            {getDuplicateAssetCompletedTitle({ isFile, isUi2026: ui2026 })}
          </FileStatusTitle>
        }
        truncate={false}
      />
    );
  },
);

DuplicateAssetTaskCompletedPanelItem.displayName = 'DuplicateAssetTaskCompletedPanelItem';
